import type { AvailableApiCategory } from '@pkg/basic'
import type {
  AsterApiType,
  BinanceApiType,
  BitbankApiType,
  BitgetApiType,
  BybitApiType,
  GateApiType,
  HyperliquidApiType,
  MexcApiType,
} from '@pkg/exchange-api'
import type { TFunction } from 'i18next'

export const getI18nApiTypeName = (params: {
  category: string
  type: string
  t: TFunction
}) => {
  const { category, type, t } = params
  const apiCategory = category as AvailableApiCategory
  switch (apiCategory) {
    case 'aster': {
      const asterType = type as AsterApiType
      switch (asterType) {
        case 'trade-history': {
          return t('exchange-api/aster/trade-history')
        }
        case 'income-history': {
          return t('exchange-api/aster/income-history')
        }
        default: {
          throw new Error(
            `Unknown Aster API type: ${asterType satisfies never}`,
          )
        }
      }
    }
    case 'binance': {
      const binanceType = type as BinanceApiType
      switch (binanceType) {
        case 'convert-history': {
          return t('exchange-api/binance/convert-history')
        }
        case 'deposit-history': {
          return t('exchange-api/binance/deposit-history')
        }
        case 'spot-trade-history': {
          return t('exchange-api/binance/spot-trade-history')
        }
        case 'usd-future-history': {
          return t('exchange-api/binance/usd-future-history')
        }
        case 'withdrawal-history': {
          return t('exchange-api/binance/withdrawal-history')
        }
        default: {
          throw new Error(
            `Unknown Binance API type: ${binanceType satisfies never}`,
          )
        }
      }
    }
    case 'bitbank': {
      const bitbankType = type as BitbankApiType
      switch (bitbankType) {
        case 'deposit-history': {
          return t('exchange-api/bitbank/deposit-history')
        }
        case 'trade-history': {
          return t('exchange-api/bitbank/trade-history')
        }
        case 'withdrawal-history': {
          return t('exchange-api/bitbank/withdrawal-history')
        }
        default: {
          throw new Error(
            `Unknown Bitbank API type: ${bitbankType satisfies never}`,
          )
        }
      }
    }
    case 'bitget': {
      const bitgetType = type as BitgetApiType
      switch (bitgetType) {
        case 'deposit-history': {
          return t('exchange-api/bitget/deposit-history')
        }
        case 'spot-tax-history': {
          return t('exchange-api/bitget/spot-tax-history')
        }
        case 'withdrawal-history': {
          return t('exchange-api/bitget/withdrawal-history')
        }
        default: {
          throw new Error(
            `Unknown Bitget API type: ${bitgetType satisfies never}`,
          )
        }
      }
    }
    case 'bybit': {
      const bybitType = type as BybitApiType
      switch (bybitType) {
        case 'deposit-history': {
          return t('exchange-api/bybit/deposit-history')
        }
        case 'future-linear-history': {
          return t('exchange-api/bybit/future-linear-history')
        }
        case 'spot-trade-history': {
          return t('exchange-api/bybit/spot-trade-history')
        }
        case 'uta-transaction-history': {
          return t('exchange-api/bybit/uta-transaction-history')
        }
        case 'withdrawal-history': {
          return t('exchange-api/bybit/withdrawal-history')
        }
        default: {
          throw new Error(
            `Unknown Bybit API type: ${bybitType satisfies never}`,
          )
        }
      }
    }
    case 'gate': {
      const gateType = type as GateApiType
      switch (gateType) {
        case 'deposit-history': {
          return t('exchange-api/gate/deposit-history')
        }
        case 'futures-change-history': {
          return t('exchange-api/gate/futures-change-history')
        }
        case 'spot-trade-history': {
          return t('exchange-api/gate/spot-trade-history')
        }
        case 'withdrawal-history': {
          return t('exchange-api/gate/withdrawal-history')
        }
        default: {
          throw new Error(`Unknown Gate API type: ${gateType satisfies never}`)
        }
      }
    }
    case 'hyperliquid': {
      const hyperliquidType = type as HyperliquidApiType
      switch (hyperliquidType) {
        case 'fill-history': {
          return t('exchange-api/hyperliquid/fill-history')
        }
        case 'funding-history': {
          return t('exchange-api/hyperliquid/funding-history')
        }
        case 'ledger-update-history': {
          return t('exchange-api/hyperliquid/ledger-update-history')
        }
        case 'staking-reward-history': {
          return t('exchange-api/hyperliquid/staking-reward-history')
        }
        default: {
          throw new Error(
            `Unknown Hyperliquid API type: ${hyperliquidType satisfies never}`,
          )
        }
      }
    }
    case 'mexc': {
      const mexcType = type as MexcApiType
      switch (mexcType) {
        case 'deposit-history': {
          return t('exchange-api/mexc/deposit-history')
        }
        case 'spot-trade-history': {
          return t('exchange-api/mexc/spot-trade-history')
        }
        case 'withdrawal-history': {
          return t('exchange-api/mexc/withdrawal-history')
        }
        default: {
          throw new Error(`Unknown MEXC API type: ${mexcType satisfies never}`)
        }
      }
    }
    default: {
      throw new Error(
        `Unsupported API category: ${apiCategory satisfies never}`,
      )
    }
  }
}

export const getI18nApiTypeDescription = (params: {
  category: string
  type: string
  t: TFunction
}) => {
  const { category, type, t } = params
  const apiCategory = category as AvailableApiCategory
  switch (apiCategory) {
    case 'aster': {
      const asterType = type as AsterApiType
      switch (asterType) {
        case 'trade-history': {
          return t('exchange-api/aster/trade-history/description')
        }
        case 'income-history': {
          return t('exchange-api/aster/income-history/description')
        }
        default: {
          throw new Error(
            `Unknown Aster API type: ${asterType satisfies never}`,
          )
        }
      }
    }
    case 'binance': {
      const binanceType = type as BinanceApiType
      switch (binanceType) {
        case 'convert-history': {
          return t('exchange-api/binance/convert-history/description')
        }
        case 'deposit-history': {
          return t('exchange-api/binance/deposit-history/description')
        }
        case 'spot-trade-history': {
          return t('exchange-api/binance/spot-trade-history/description')
        }
        case 'usd-future-history': {
          return t('exchange-api/binance/usd-future-history/description')
        }
        case 'withdrawal-history': {
          return t('exchange-api/binance/withdrawal-history/description')
        }
        default: {
          throw new Error(
            `Unknown Binance API type: ${binanceType satisfies never}`,
          )
        }
      }
    }
    case 'bitbank': {
      const bitbankType = type as BitbankApiType
      switch (bitbankType) {
        case 'deposit-history': {
          return t('exchange-api/bitbank/deposit-history/description')
        }
        case 'trade-history': {
          return t('exchange-api/bitbank/trade-history/description')
        }
        case 'withdrawal-history': {
          return t('exchange-api/bitbank/withdrawal-history/description')
        }
        default: {
          throw new Error(
            `Unknown Bitbank API type: ${bitbankType satisfies never}`,
          )
        }
      }
    }
    case 'bitget': {
      const bitgetType = type as BitgetApiType
      switch (bitgetType) {
        case 'deposit-history': {
          return t('exchange-api/bitget/deposit-history/description')
        }
        case 'spot-tax-history': {
          return t('exchange-api/bitget/spot-tax-history/description')
        }
        case 'withdrawal-history': {
          return t('exchange-api/bitget/withdrawal-history/description')
        }
        default: {
          throw new Error(
            `Unknown Bitget API type: ${bitgetType satisfies never}`,
          )
        }
      }
    }
    case 'bybit': {
      const bybitType = type as BybitApiType
      switch (bybitType) {
        case 'deposit-history': {
          return t('exchange-api/bybit/deposit-history/description')
        }
        case 'future-linear-history': {
          return t('exchange-api/bybit/future-linear-history/description')
        }
        case 'spot-trade-history': {
          return t('exchange-api/bybit/spot-trade-history/description')
        }
        case 'uta-transaction-history': {
          return t('exchange-api/bybit/uta-transaction-history/description')
        }
        case 'withdrawal-history': {
          return t('exchange-api/bybit/withdrawal-history/description')
        }
        default: {
          throw new Error(
            `Unknown Bybit API type: ${bybitType satisfies never}`,
          )
        }
      }
    }
    case 'gate': {
      const gateType = type as GateApiType
      switch (gateType) {
        case 'deposit-history': {
          return t('exchange-api/gate/deposit-history/description')
        }
        case 'futures-change-history': {
          return t('exchange-api/gate/futures-change-history/description')
        }
        case 'spot-trade-history': {
          return t('exchange-api/gate/spot-trade-history/description')
        }
        case 'withdrawal-history': {
          return t('exchange-api/gate/withdrawal-history/description')
        }
        default: {
          throw new Error(`Unknown Gate API type: ${gateType satisfies never}`)
        }
      }
    }
    case 'hyperliquid': {
      const hyperliquidType = type as HyperliquidApiType
      switch (hyperliquidType) {
        case 'fill-history': {
          return t('exchange-api/hyperliquid/fill-history/description')
        }
        case 'funding-history': {
          return t('exchange-api/hyperliquid/funding-history/description')
        }
        case 'ledger-update-history': {
          return t('exchange-api/hyperliquid/ledger-update-history/description')
        }
        case 'staking-reward-history': {
          return t(
            'exchange-api/hyperliquid/staking-reward-history/description',
          )
        }
        default: {
          throw new Error(
            `Unknown Hyperliquid API type: ${hyperliquidType satisfies never}`,
          )
        }
      }
    }
    case 'mexc': {
      const mexcType = type as MexcApiType
      switch (mexcType) {
        case 'deposit-history': {
          return t('exchange-api/mexc/deposit-history/description')
        }
        case 'spot-trade-history': {
          return t('exchange-api/mexc/spot-trade-history/description')
        }
        case 'withdrawal-history': {
          return t('exchange-api/mexc/withdrawal-history/description')
        }
        default: {
          throw new Error(`Unknown MEXC API type: ${mexcType satisfies never}`)
        }
      }
    }
    default: {
      throw new Error(
        `Unsupported API category: ${apiCategory satisfies never}`,
      )
    }
  }
}
